import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { ReactComponent as CloseIcon } from 'assets/cross.icon.svg';

import { ITicketTemplate } from '../../services/ticketTemplates/types';
import {
  ThunkCreateTicketFromTemplate,
  ThunkGetTicketTemplate,
} from '../../store/ticketTemplates/thunks';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  selectClientPortalCompanies,
  selectClientPortalConfigurations,
} from '../../store/ticketTemplates/selectors';
import { ThunkClientPortalConfigurations } from '../../store/ticketTemplates/thunks/clientPortalConfigurationsOptions';
import { UserRoleSelector } from '../../store/user/selector';
import { ThunkClientPortalCompanies } from '../../store/ticketTemplates/thunks/clientPortalCompanies';

import TicketTemplateItem from './components/TicketTemplateItem';
import RewstIframe from './components/RewstIframe';
import Button from '../../components/Button/Button';
import Loader from '../../components/Loader';
import Select from '../../components/Select';

import { TICKET, TICKET_CREATE } from '../../router/routes';
import { LOCAL_STORAGE_PSA } from '../../constants/storage';
import { Roles } from '../../constants/roles';
import { createQuestionsValidationSchema } from './form';

import useStyles from './styles';
import { tt } from '@i18n/tt';
import { REWST_TEMPLATE_TYPE } from 'constants/ticketTemplates';

interface IProps {
  useTeamsStyles?: boolean;
}

const TicketTemplate: React.FC<IProps> = ({ useTeamsStyles }) => {
  const [ticketTemplate, setTicketTemplate] = useState<ITicketTemplate>();
  const [loading, setLoading] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);

  const configurationOptions = useSelector(selectClientPortalConfigurations);
  const companyOptions = useSelector(selectClientPortalCompanies);

  const classes = useStyles();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { ticketTemplateId } = useParams();
  const role = useAppSelector(UserRoleSelector);

  const handleBack = useCallback(() => {
    navigate(TICKET_CREATE);
  }, [navigate]);

  const { isConnectWiseWithConfigItems, isConnectWiseAndNotUser } = useMemo(() => {
    const isConnectWise = localStorage.getItem(LOCAL_STORAGE_PSA) === 'Connectwise';
    return {
      isConnectWiseWithConfigItems:
        isConnectWise && ticketTemplate?.showConfigurationItemsDropdown && role === Roles.User,
      isConnectWiseAndNotUser: isConnectWise && role !== Roles.User,
    };
  }, [ticketTemplate, role]);

  const initialQuestions =
    ticketTemplate?.questions?.reduce(
      (acc, question) => {
        acc[question.id] = '';
        return acc;
      },
      {} as Record<string, any>,
    ) ?? {};

  const formik = useFormik({
    initialValues: {
      questions: initialQuestions,
      configurationId: undefined,
      companyId: undefined,
    },
    initialErrors: {
      questions: {},
    },
    onSubmit: values => {
      setProcessing(true);
      const payload: {
        companyId?: number;
        configurationId?: string;
        ticketTemplateId: number | undefined;
        questions: Array<{ id: number; value: unknown }>;
      } = {
        questions: Object.entries(values.questions).map(i => ({ id: Number(i[0]), value: i[1] })),
        ticketTemplateId: ticketTemplate?.id,
        configurationId: values.configurationId,
        companyId: values.companyId,
      };

      dispatch(ThunkCreateTicketFromTemplate({ payload }))
        .unwrap()
        .then(res => {
          navigate(TICKET.replace(':ticketId', res.data));
        })
        .finally(() => {
          setProcessing(false);
        });
    },
    validationSchema: createQuestionsValidationSchema(ticketTemplate),
    enableReinitialize: true,
  });

  const { values, handleSubmit, errors, touched, handleBlur, handleChange } = formik;

  const onMount = useCallback(() => {
    if (isConnectWiseWithConfigItems) {
      dispatch(ThunkClientPortalConfigurations());
    }
    if (isConnectWiseAndNotUser) {
      dispatch(ThunkClientPortalCompanies());
    }
    if (Number(ticketTemplateId)) {
      setLoading(true);
      dispatch(ThunkGetTicketTemplate({ ticketTemplateId: Number(ticketTemplateId) }))
        .unwrap()
        .then(res => {
          setTicketTemplate(res);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isConnectWiseAndNotUser, isConnectWiseWithConfigItems]);

  useEffect(() => {
    onMount();
  }, [onMount]);

  const isRewstTemplate = ticketTemplate?.type === REWST_TEMPLATE_TYPE;

  useEffect(() => {
    console.log(ticketTemplate);
  }, [ticketTemplate]);

  if (loading) return <Loader />;

  if (isRewstTemplate) {
    return (
      <Dialog
        open
        onClose={handleBack}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            margin: 0,
            maxWidth: '100%',
            maxHeight: '100%',
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handleBack}
          sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          <RewstIframe
            url={ticketTemplate?.questions[0].values?.[0] ?? ''}
            title={ticketTemplate?.title}
            fullScreen
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={useTeamsStyles ? classes.formTeams : classes.form}>
      <div className={classes.formHeader}>
        <div>{ticketTemplate?.title}</div>
        <div>
          <Button text={tt('buttons.back')} onClick={handleBack} />
        </div>
      </div>
      <div className={classes.itemsList}>
        {isConnectWiseAndNotUser && Boolean(companyOptions.length) && (
          <Select
            onChange={handleChange}
            value={formik.values.companyId}
            label="Select customer"
            name="companyId"
            options={companyOptions}
            error={Boolean(touched.companyId && errors.companyId)}
            helperText={errors.companyId as string}
            onBlur={handleBlur}
          />
        )}
        {isConnectWiseWithConfigItems && (
          <Select
            onChange={formik.handleChange}
            value={values.configurationId}
            label="Select configuration"
            name="configurationId"
            options={configurationOptions}
          />
        )}
        {ticketTemplate?.questions.map(q => (
          <TicketTemplateItem key={q.id} {...q} formik={formik} disabled={processing} />
        ))}
      </div>
      <div>
        <Button
          text={tt('buttons.submit')}
          type="submit"
          disabled={processing}
          loading={processing}
        />
      </div>
    </form>
  );
};

export default TicketTemplate;
